import { ReactElement, useContext } from "react";
import CheckOutCalendar from "../../pages/CheckOut/CheckOutCalendar";
import { ReactComponent as Tick } from "../../assets/icons/round_check.svg";
import { getTimeCellInfomation } from "../../utilities/cleanerTime";
import { ReactComponent as CloseCircle } from "../../assets/icons/close_circle.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow_down.svg";
import {
  cleaner,
  orderDetailsType,
  settings,
  timeCell,
  workSchedule
} from "../../state_management/actionTypes";
import { orderInfo, orderType } from "../../state_management/reducers/order";
import { asyncResType } from "../../utilities/asyncFunc";
import {
  checkAddressParams,
  disabledDateParams,
  getCleanerTimeParams
} from "../../state_management/epics/order";
import "./CheckOutSection.css";
import { LocaleContext } from "../../AppProvider";

type checkout_section = {
  Buttons: ReactElement;
  time_set: (fullTime: string, Date: Date) => void;
  timeArray: workSchedule[] | undefined;
  date: Date | undefined;
  disabledDatesRes: asyncResType<
    disabledDateParams,
    | {
        disabled_days: string[];
      }
    | undefined
  >;
  setDate: (date: Date) => void;
  setMonth: (month: Date) => void;
  disabledDateObj:
    | Partial<{
        month: number;
        data: string[];
      }>
    | undefined;
  cleaner: string;
  settings: Partial<settings>;
  cleanerError: boolean;
  availableTimeRes: asyncResType<getCleanerTimeParams, timeCell[] | undefined>;
} & (
  | {
      time?: never;
      //
      reschedule: false;
      address: string;
      orderDetails: orderType;
      address_field_id: string;
      clearAddress: () => void;
      setOpenEmployee: (data: boolean) => void;
      openEmployee: boolean;
      orderInfo: orderInfo;
      setOrderDetails: (data: Partial<orderType>) => void;
      serviceDuration: number;
      Errors: ReactElement;
      employee_list_id: string;
      addressBlackListCheckRes: asyncResType<
        checkAddressParams,
        cleaner[] | undefined
      >;
      cleaners: cleaner[] | undefined;
    }
  | {
      clearAddress?: never;
      setOpenEmployee?: never;
      openEmployee?: never;
      orderInfo?: never;
      setOrderDetails?: never;
      Errors?: never;
      employee_list_id?: never;
      addressBlackListCheckRes?: never;
      cleaners?: never;
      address_field_id?: never;
      //
      address?: never;
      reschedule: true;
      orderDetails: orderDetailsType["data"];
      serviceDuration: number;
      time: string | undefined;
    }
);

const CheckOutSection = ({
  children,
  reschedule,
  orderDetails,
  address_field_id,
  time_set,
  timeArray,
  date,
  address,
  clearAddress,
  disabledDatesRes,
  setDate,
  setMonth,
  disabledDateObj,
  cleaner,
  setOpenEmployee,
  openEmployee,
  settings,
  orderInfo,
  setOrderDetails,
  serviceDuration,
  time,
  Buttons,
  Errors,
  employee_list_id,
  addressBlackListCheckRes,
  cleaners,
  cleanerError,
  availableTimeRes
}: checkout_section & { children: ReactElement }) => {
  const { t } = useContext(LocaleContext);
  const months: string[] = [
    t("Jan"),
    t("Feb"),
    t("Mar"),
    t("Apr"),
    t("May"),
    t("Jun"),
    t("Jul"),
    t("Aug"),
    t("Sep"),
    t("Oct"),
    t("Nov"),
    t("Dec")
  ];
  const weekDays: string[] = [
    t("Sun"),
    t("Mon"),
    t("Tue"),
    t("Wed"),
    t("Thur"),
    t("Fri"),
    t("Sat")
  ];

  const details = ((): orderType => {
    if (reschedule) {
      return {
        cleaner_id: orderDetails.cleaner_id,
        client_address: orderDetails.customer.address,
        client_email: orderDetails.customer.email,
        client_firstname: orderDetails.customer.firstname,
        client_lastname: orderDetails.customer.lastname,
        client_mobile: orderDetails.customer.mobile,
        coupon_code: orderDetails["coupon code"],
        full_payment: orderDetails.full_payment,
        info: orderDetails.info,
        latitude: orderDetails.customer.latitude,
        longitude: orderDetails.customer.longitude,
        random_cleaner: !!orderDetails.random_cleaner,
        time_start: orderDetails.time_start
      };
    } else {
      return orderDetails;
    }
  })();

  const cellInfo = (timeObj: {
    time: number;
    free: boolean;
  }): Partial<ReturnType<typeof getTimeCellInfomation>> => {
    if (!date) {
      return {};
    }

    if (reschedule) {
      return getTimeCellInfomation({
        reschedule: true,
        date,
        orderDetails,
        orderInfo: orderDetails,
        serviceDuration,
        settings,
        timeObj,
        selected_reschedule_time: time
      });
    } else {
      return getTimeCellInfomation({
        setOrderDetails,
        orderWindow: settings.order_creation_before_interval,
        date,
        orderDetails,
        orderInfo,
        serviceDuration,
        timeObj
      });
    }
  };

  const inputValue = address_field_id
    ? (document.getElementById(address_field_id) as
        | HTMLInputElement
        | undefined)
    : undefined;

  return details ? (
    <div className="flex checkout_content_wrapper">
      <div className="checkout_left">
        <div>
          <p className="reg_text time_slot_text">{t("Please choose a time slot by clicking on it.")}</p>
          <p className="reg_text time_slot_text">
            {t(
              "Book with confidence: you can reschedule for free up to 24 hours before your scheduled appointment."
            )}
          </p>
          {!address && !reschedule ? (
            <p className="fontRed">
              {t(
                "Please start entering your address and select one from the suggestions list."
              )}
            </p>
          ) : (
            <></>
          )}

          <div className="checkout_content">
            <div className="checkout_time_address_wrapper flex">
              <input
                className="checkout_time_address flex1"
                type="text"
                name="address"
                id={address_field_id}
                autoComplete="off"
                placeholder={t("Enter your address…")}
                value={reschedule ? orderDetails.customer.address : undefined}
                readOnly={reschedule ? true : undefined}
              />
              <span className="center">
                {(inputValue?.value || address) && !reschedule ? (
                  <CloseCircle onClick={clearAddress} />
                ) : (
                  <></>
                )}
              </span>
            </div>
            <CheckOutCalendar
              disabledCalendar={reschedule ? false : !address}
              date={date}
              disabledDateObj={disabledDateObj}
              isLoading={disabledDatesRes.loading}
              setDate={setDate}
              setMonth={setMonth}
            />
            {details.client_address && (
              <div className="cleaner_pick_wrapper">
                <div className="cleaner_selected_wrapper">
                  {orderDetails.cleaner_id || orderDetails.random_cleaner ? (
                    <>
                      <span>{t("Selected Cleaner")}: </span>
                      <div className="med_text fontBlue">
                        {!reschedule && orderDetails.random_cleaner
                          ? t("Any")
                          : cleaner}
                      </div>
                    </>
                  ) : (
                    <span>{t("No selected Cleaner")}</span>
                  )}
                </div>
                {reschedule ? (
                  <></>
                ) : (
                  <>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenEmployee(!openEmployee);
                      }}
                      className="fontBlue"
                    >
                      {t("Have a favorite cleaner?")}{" "}
                      <ArrowDownIcon className="arrowIcon" />
                    </span>
                    {openEmployee && !reschedule ? (
                      <div id={employee_list_id} className="cleaner_list">
                        <div
                          onClick={() => {
                            setOrderDetails({
                              ...orderDetails,
                              cleaner_id: null,
                              random_cleaner: true
                            });
                            setOpenEmployee(false);
                          }}
                          className={`item flex ${
                            orderDetails.random_cleaner ? `selected` : ``
                          }`}
                        >
                          <span>{t("Any")}</span>
                        </div>

                        {addressBlackListCheckRes.loading ? (
                          <div className={`item flex grayed`}>
                            <span>{t("Loading...")}</span>
                          </div>
                        ) : (
                          (cleaners || []).map((cleaner, n) => (
                            <div
                              onClick={() => {
                                setOrderDetails({
                                  ...orderDetails,
                                  cleaner_id: cleaner.id,
                                  random_cleaner: false
                                });
                                setOpenEmployee(false);
                              }}
                              key={n}
                              className={`item flex ${
                                orderDetails.cleaner_id === cleaner.id
                                  ? `selected`
                                  : ``
                              }`}
                            >
                              <span>
                                {cleaner.firstname} {cleaner.lastname}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    ) : (
                      <div />
                    )}
                  </>
                )}
              </div>
            )}
            {!reschedule &&
            !addressBlackListCheckRes.loading &&
            !availableTimeRes.loading &&
            !disabledDatesRes.loading &&
            details.client_address &&
            Array.isArray(cleaners) &&
            !cleaners.length ? (
              <div>{t("There is no cleaner available.")}</div>
            ) : details.client_address && date ? (
              <>
                <div className="date_content">
                  {weekDays[date.getDay()]}, {months[date.getMonth()]}{" "}
                  {date.getDate()}
                  {new Date().getFullYear() !== date.getFullYear()
                    ? ` (${date.getFullYear()})`
                    : ``}
                </div>
                <div style={{ minHeight: 160 }}>
                  {cleanerError && !availableTimeRes.loading ? (
                    <div>{t("This cleaner is not available on this day.")}</div>
                  ) : !availableTimeRes.loading ? (
                    <>
                      <div className="time_content_wrapper">
                        {timeArray?.map((timeObj, n) => {
                          const { inactive, isSelected, Date, Time, fullTime } =
                            cellInfo(timeObj);

                          return (
                            <div
                              onClick={() => {
                                if (inactive) {
                                  return;
                                } else if (fullTime && Date) {
                                  time_set(fullTime, Date);
                                }
                              }}
                              key={`${n}-${date.toISOString()}`}
                              className={`time_content_item flex ${
                                inactive ? `strike_through inactive` : ``
                              } ${isSelected ? `selected` : ``}`}
                            >
                              <div
                                className={`tick_wrapper ${
                                  isSelected ? `` : `inactive`
                                }`}
                              >
                                {isSelected && (
                                  <Tick className="img_div_contain" />
                                )}
                              </div>
                              {Time}
                            </div>
                          );
                        })}
                      </div>
                      {timeArray?.length ? (
                        <div className="availTime_help_corner">
                          <div>
                            {t("Can't find available time or need help?")}
                          </div>
                          <a
                            className="fontBlue"
                            href={`tel:${t("help_num").replace(
                              /\)|\(|\s/g,
                              ""
                            )}`}
                          >
                            {t(`Give us a call: {{number}}`, {
                              "{{number}}": t("help_num")
                            })}
                          </a>
                        </div>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <div>{t("Loading...")}</div>
                  )}
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
          {Buttons ? Buttons : <></>}
          {Errors ? Errors : <></>}
        </div>
      </div>
      <div className="checkout_right">{children}</div>
    </div>
  ) : (
    <div />
  );
};

export default CheckOutSection;
